import { formatDateTask } from './taskUtils';

/**
 * Task status utility functions
 */

/**
 * Get task styling based on status and dates
 * @param {Object} taskData - Task data object
 * @returns {Object} Object containing background and border colors
 */
export function getTaskStyling(taskData) {
  if (!taskData) {
    return { backgroundTask: '#fff', borderColorTask: '#000' };
  }

  const today = new Date().toISOString().split('T')[0];
  let backgroundTask = '#fff';
  let borderColorTask = '#000';

  // Priority order for status styling
  if (taskData.status === 'outdated') {
    backgroundTask = '#FDD2D2';
    borderColorTask = '#963333';
  } else if (formatDateTask(taskData.updatedAt) === today) {
    backgroundTask = '#FAF7E7';
    borderColorTask = '#D4BD35';
  } else if (taskData.status === 'not perfomed') {
    backgroundTask = '#FFF';
    borderColorTask = '#000';
  } else if (taskData.status === 'incompleted') {
    backgroundTask = '#FFEBEB';
    borderColorTask = '#B44F4F';
  } else if (taskData.status === 'completed') {
    if (taskData.maintenanceName === 'Report a repair') {
      backgroundTask = '#EDF9FF';
      borderColorTask = '#3F97BE';
    } else if (formatDateTask(taskData.updatedAt) > formatDateTask(taskData.createdAt)) {
      backgroundTask = '#FFEBEB';
      borderColorTask = '#B44F4F';
    } else {
      backgroundTask = '#EDF8F2';
      borderColorTask = '#4FB47E';
    }
  }

  return { backgroundTask, borderColorTask };
}

/**
 * Get status badge styling and text
 * @param {boolean} isTaskCompletedOnTime - Whether task was completed on time
 * @param {string} maintenanceName - Maintenance task name
 * @param {string} status - Task status
 * @param {Function} t - Translation function
 * @returns {Object} Badge configuration object
 */
export function getStatusBadgeConfig(isTaskCompletedOnTime, maintenanceName, status, t) {
  if (maintenanceName === 'Report a repair') {
    return {
      backgroundColor: '#EDF9FF',
      border: '1px solid #3F97BE',
      text: t('modals.repair'),
    };
  }

  if (isTaskCompletedOnTime === true) {
    return {
      backgroundColor: '#EDF8F2',
      border: '1px solid #4FB47E',
      text: t('calendar.completedEarlier'),
    };
  }

  if (isTaskCompletedOnTime === false) {
    return {
      backgroundColor: '#F8EDED',
      border: '1px solid #B44F4F',
      text: status !== 'incompleted' ? t('calendar.completedLate') : t('calendar.incomplete'),
    };
  }

  return null;
}

/**
 * Check if task is in read-only mode
 * @param {Object} taskData - Task data object
 * @returns {boolean} True if task is read-only
 */
export function isTaskReadonly(taskData) {
  if (!taskData) return false;
  
  return (
    taskData.status === 'not perfomed' &&
    !isTaskOutdated(taskData.updatedAt, taskData.notifyTime)
  );
}

/**
 * Check if task is outdated based on notification lead time
 * @param {string} taskDate - Task date
 * @param {number} interval - Notification lead time in days
 * @returns {boolean} True if task is outdated
 */
function isTaskOutdated(taskDate, interval) {
  const currentDate = new Date();
  const taskUpdatedAt = new Date(taskDate);
  taskUpdatedAt.setDate(taskUpdatedAt.getDate() - interval);
  return taskUpdatedAt < currentDate;
}